import React from 'react';
import { Check, Lock, Lightbulb, PanelLeftClose, Pin } from 'lucide-react';
import { RobbieMark } from '../ui';

/**
 * v2 journey rail (TASK-106) — the wizard's left rail, ported from the
 * `RFQ Wizard v2*.dc.html` mocks: steps 1–5 with number/check/active states,
 * step 3 expanding sub-items (Context / Validation / Discovery) with dot
 * states, a locked COMING SOON group (honest, unclickable), and the
 * "Tip from Robbie" card slot pinned at the bottom. Presentational; the page
 * derives states from the RFQ state machine and owns navigation (URL routes
 * per TASK-1007). Width comes from the parent (`w-step-rail`).
 */
/**
 * `visited` = navigable but its work is not data-complete (revisitable steps
 * behind the journey frontier) — numbered like upcoming, styled clickable.
 */
export type JourneyState = 'done' | 'active' | 'visited' | 'upcoming';

export interface JourneySubStep {
  id: string;
  label: string;
  state: JourneyState;
}

export interface JourneyStep {
  id: string;
  label: string;
  state: JourneyState;
  /** One-line "why not done yet" — shown as tooltip on upcoming steps. */
  reason?: string;
  /** Rendered indented under the step while it is active (step 3's 3a/3b/3c). */
  subSteps?: JourneySubStep[];
}

export interface JourneyRailProps {
  steps: JourneyStep[];
  /** Locked future stages (Financing, Delivery, Installation, Contracting). */
  locked?: string[];
  onStepSelect?: (id: string) => void;
  onSubStepSelect?: (id: string) => void;
  /** "Tip from Robbie" card slot; use TipFromRobbie for the shipped pattern. */
  tip?: React.ReactNode;
  /**
   * Pin affordance next to the "RFQ Journey" label: pinned = docked in flow,
   * unpinned = this is the hover flyout and the control pins it. Omit
   * `onTogglePin` on a rail that cannot collapse.
   */
  pinned?: boolean;
  onTogglePin?: () => void;
  className?: string;
}

/**
 * The step marker itself — number, tick or active pill. Exported because the
 * collapsed rail (`JourneyRailSlim`) draws the very same badge: a step must
 * not change what it says about itself just because the labels went away.
 */
export const StepBadge: React.FC<{ index: number; state: JourneyState }> = ({ index, state }) => {
  if (state === 'done') {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-round border border-success-border bg-success-bg text-success-ink">
        <Check className="h-[11px] w-[11px]" />
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-round bg-action text-[11px] font-bold text-white">
        {index}
      </span>
    );
  }
  if (state === 'visited') {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-round border border-line-strong text-[11px] font-semibold text-ink-secondary">
        {index}
      </span>
    );
  }
  return (
    <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-round border border-line-strong text-[11px] font-semibold text-ink-muted">
      {index}
    </span>
  );
};

const SubStepRow: React.FC<{ sub: JourneySubStep; onSelect?: (id: string) => void }> = ({ sub, onSelect }) => {
  const dot =
    sub.state === 'done' ? 'bg-success-ink' : sub.state === 'active' ? 'bg-action' : 'bg-line-strong';
  const row =
    sub.state === 'active'
      ? 'rounded-[6px] bg-action-bg font-semibold text-action-deep'
      : 'font-medium text-ink-muted';
  return (
    <button
      type="button"
      onClick={() => onSelect?.(sub.id)}
      aria-current={sub.state === 'active' ? 'step' : undefined}
      className={`ml-s3 flex items-center gap-s3 px-s3 py-[4px] text-left text-body-sm ${row}`}
    >
      <span className={`h-[6px] w-[6px] shrink-0 rounded-round ${dot}`} />
      {sub.label}
    </button>
  );
};

export const JourneyRail: React.FC<JourneyRailProps> = ({
  steps,
  locked = [],
  onStepSelect,
  onSubStepSelect,
  tip,
  pinned = false,
  onTogglePin,
  className = '',
}) => (
  <aside className={`flex h-full shrink-0 flex-col overflow-y-auto border-r border-line bg-surface px-[12px] py-s6 ${className}`}>
    <div className="mb-s4 flex items-center gap-s3 px-s3">
      <span className="text-rail-label uppercase text-[#98A2B3]">RFQ Journey</span>
      {onTogglePin && (
        <button
          type="button"
          onClick={onTogglePin}
          aria-label={pinned ? 'Collapse the RFQ journey to the rail' : 'Keep the RFQ journey open'}
          aria-expanded={pinned}
          aria-pressed={pinned}
          title={pinned ? 'Collapse the RFQ journey to the rail' : 'Keep the RFQ journey open'}
          className="-mr-s2 ml-auto flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-control text-ink-muted hover:bg-canvas hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
        >
          {pinned ? <PanelLeftClose className="h-[14px] w-[14px]" /> : <Pin className="h-[14px] w-[14px]" />}
        </button>
      )}
    </div>
    <div className="flex flex-col gap-[2px]">
      {steps.map((step, i) => (
        <React.Fragment key={step.id}>
          <button
            type="button"
            onClick={() => onStepSelect?.(step.id)}
            aria-current={step.state === 'active' ? 'step' : undefined}
            title={step.state === 'upcoming' || step.state === 'visited' ? step.reason : undefined}
            className={`relative flex items-center gap-s4 rounded-control px-[9px] py-s3 text-left text-body-md ${
              step.state === 'active' ? 'bg-action-bg font-semibold text-action-deep' : 'font-medium text-ink-secondary hover:bg-canvas'
            } ${step.state === 'upcoming' ? 'cursor-default opacity-70' : ''}`}
          >
            {step.state === 'active' && <span className="absolute bottom-[7px] left-0 top-[7px] w-[2.5px] rounded-[2px] bg-action" />}
            <StepBadge index={i + 1} state={step.state} />
            {step.label}
          </button>
          {step.subSteps && step.subSteps.length > 0 && (
            <div className="relative flex flex-col gap-px pb-[4px] pl-[20px] pt-[2px]">
              <span className="absolute bottom-[10px] left-[19px] top-[6px] w-px bg-line" />
              {step.subSteps.map((sub) => (
                <SubStepRow key={sub.id} sub={sub} onSelect={onSubStepSelect} />
              ))}
            </div>
          )}
        </React.Fragment>
      ))}
      {locked.length > 0 && (
        <>
          <div className="flex items-center gap-s3 px-s2 pb-s2 pt-[12px]">
            <div className="h-px flex-1 bg-neutral-border" />
            <span className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-[#98A2B3]">Coming soon</span>
            <div className="h-px flex-1 bg-neutral-border" />
          </div>
          {locked.map((label) => (
            <div key={label} className="flex items-center gap-s4 px-[9px] py-[7px] opacity-55" aria-disabled="true">
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-round bg-canvas text-[#98A2B3]">
                <Lock className="h-[10px] w-[10px]" />
              </span>
              <span className="text-body-md font-medium text-[#98A2B3]">{label}</span>
            </div>
          ))}
        </>
      )}
    </div>
    {tip && <div className="mt-auto pt-s5">{tip}</div>}
  </aside>
);

/** The shipped "Tip from Robbie" card — AI-chip wash, mascot + bulb signal. */
export const TipFromRobbie: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-card border border-robbie-border bg-ai-chip px-[12px] py-[11px]">
    <div className="mb-[5px] flex items-center gap-[7px]">
      <span className="relative inline-flex shrink-0">
        <RobbieMark size="xs" aria-label="Robbie" />
        <span className="absolute -right-[5px] -top-[6px] flex h-[12px] w-[12px] items-center justify-center rounded-round bg-signal shadow-azure-glow">
          <Lightbulb className="h-s3 w-s3 text-white" />
        </span>
      </span>
      <span className="text-meta font-bold text-robbie-ink">Tip from Robbie</span>
    </div>
    <p className="m-0 text-meta leading-[1.5] text-ink-secondary">{children}</p>
  </div>
);

export default JourneyRail;
